import { useState, type CSSProperties } from 'react';
import { useStore } from './state/store';
import { hasWebGL2 } from './lib/capabilities';
import { DropZone } from './components/DropZone';
import { FileList } from './components/FileList';
import { Viewer } from './components/Viewer';
import { PipelinePanel } from './components/PipelinePanel';
import { SepPanel } from './components/SepPanel';
import { MockupPanel } from './components/MockupPanel';
import { InspectorPanel } from './components/InspectorPanel';
import { SettingsBar } from './components/SettingsBar';
import { ExportBar } from './components/ExportBar';
import { PanelFooter } from './components/PanelFooter';
import { PanelResizer } from './components/PanelResizer';
import { ModeSwitch } from './components/ModeSwitch';
import { UndoToast } from './components/UndoToast';
import type { AppMode } from './state/sep';

const MODE_HINTS: Record<AppMode, string> = {
  dtf: 'DTF print prep · everything stays on this computer',
  sep: 'Screen-print separations · everything stays on this computer',
  mockup: 'Garment mockups · everything stays on this computer',
};

export function App() {
  const engineError = useStore((s) => s.engineError);
  const appMode = useStore((s) => s.appMode);
  const activeId = useStore((s) => s.activeId);
  const glPreview = useStore((s) => s.glPreview);
  /** The side panel's width, straight into the grid as `--panel-w`: the panel is
   *  a grid TRACK, so nothing but the grid can size it, and a custom property is
   *  how a React component gets a number into a stylesheet's track list. */
  const panelWidth = useStore((s) => s.viewer.panelWidth);
  /** Dismissed for this session only, and deliberately not persisted: the reason the renderer is
   *  slow (a blocklisted GPU, hardware acceleration off, a context the browser took away) can change
   *  between loads, and a notice silenced for good would be a notice nobody ever acts on. */
  const [glDismissed, setGlDismissed] = useState(false);
  // Only in mockup mode — it is the only preview the GPU path draws — and only once there is
  // something real to say. `'cpu'` is also the state before the FIRST frame has been attempted, so
  // it alone would flash the banner on a perfectly capable browser for the moment between entering
  // mockup mode and the first GPU frame; the `hasWebGL2` test is what tells the two apart. (It is
  // reached only in mockup mode, so nothing probes for a context in the other two.)
  const glBanner = appMode === 'mockup' && !glDismissed && (glPreview === 'lost' || (glPreview === 'cpu' && !hasWebGL2()));
  return (
    <div className="app" style={{ '--panel-w': `${panelWidth}px` } as CSSProperties}>
      {engineError && <div className="banner" role="alert">{engineError}</div>}
      {glBanner && (
        <div className="banner" role="status">
          Preview is using the slower software renderer
          <button style={{ marginLeft: 10 }} onClick={() => setGlDismissed(true)}>Dismiss</button>
        </div>
      )}
      <header className="topbar">
        <span className="brand">Easy Image Prep</span>
        <ModeSwitch />
        <span className="muted">{MODE_HINTS[appMode]}</span>
      </header>
      <aside className="files" data-slot="files">
        <DropZone>
          <FileList />
        </DropZone>
      </aside>
      <main className="viewer" data-slot="viewer">
        <Viewer />
        {/* Over the canvas rather than in a bar of its own: what it is about — a
            colour, a file, a placement — is what the canvas is showing. */}
        <UndoToast />
      </main>
      <aside className="panel" data-slot="panel">
        <PanelResizer />
        {/* The scrolling half. The footer below it is pinned, so the status line
            and the mode's export never leave the screen — at 1366x768 both used
            to be below the fold. */}
        <div className="panel-body">
          {/* Keyed by the active document: SepPanel and its children (InkList,
              WhitePanel, ScreenPanel, FilmPanel) hold per-document UI-only state
              that isn't part of the separation (customSize, the mesh detail
              flag, and every draft/dirty ref for a typed field) — none of it is
              synced to `activeId`, so without a fresh mount per document it
              would leak from the previously active document onto the newly
              active one (e.g. Doc A's "Custom" film-size choice showing, and
              being editable, over Doc B's untouched preset). Remounting on
              every document switch resets all of it for free. */}
          {appMode === 'sep' ? <SepPanel key={activeId ?? 'none'} />
            : appMode === 'mockup' ? <MockupPanel key={activeId ?? 'none'} />
            : <PipelinePanel />}
          <InspectorPanel />
        </div>
        <PanelFooter />
      </aside>
      <footer className="bottombar" data-slot="bottom">
        <SettingsBar />
        <ExportBar />
      </footer>
    </div>
  );
}
